export type TaxMaintenanceRecord = {
  fromDate: string;
  toDate: string;
  isTaxAmount: boolean;
  taxAmount: number;
  isNewTaxAmount: boolean;
  newTaxAmount: number;
  taxReference: string;
  newPostNumber: string;
};

export const normalizeTaxMaintenanceRecord = (
  item: Record<string, unknown>,
): TaxMaintenanceRecord => ({
  fromDate: String(item.fromDate ?? ""),
  toDate: String(item.toDate ?? ""),
  isTaxAmount: Boolean(item.isTaxAmount),
  taxAmount: Number(item.taxAmount ?? 0),
  isNewTaxAmount: Boolean(item.isNewTaxAmount ?? false),
  newTaxAmount: Number(item.newTaxAmount ?? 0),
  taxReference: String(item.taxReference ?? ""),
  newPostNumber: String(item.newPostNumber ?? ""),
});

/** Visual One expects taxMaintenances as a JSON array string in one Excel cell. */
export const serializeTaxMaintenancesForExport = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) {
    return "[]";
  }

  return JSON.stringify(
    value.map((item) =>
      normalizeTaxMaintenanceRecord(
        item && typeof item === "object"
          ? (item as Record<string, unknown>)
          : {},
      ),
    ),
  );
};

export const buildTaxMaintenancesFromFields = (fields: {
  fromDate: string | null;
  toDate: string | null;
  isFlat: boolean;
  taxPercent: number | null;
  flatAmount: number | null;
}): TaxMaintenanceRecord[] => {
  const hasAmount = fields.isFlat
    ? fields.flatAmount != null
    : fields.taxPercent != null;

  if (!fields.fromDate && !fields.toDate && !hasAmount) {
    return [];
  }

  return [
    {
      fromDate: fields.fromDate ?? "",
      toDate: fields.toDate ?? "",
      isTaxAmount: fields.isFlat,
      taxAmount: Number(
        fields.isFlat ? (fields.flatAmount ?? 0) : (fields.taxPercent ?? 0),
      ),
      isNewTaxAmount: false,
      newTaxAmount: 0,
      taxReference: "",
      newPostNumber: "",
    },
  ];
};

export const readTaxMaintenanceFields = (
  maintenances: unknown,
): {
  fromDate: string | null;
  toDate: string | null;
  isFlat: boolean;
  taxPercent: number | null;
  flatAmount: number | null;
} | null => {
  if (!Array.isArray(maintenances) || maintenances.length === 0) {
    return null;
  }

  const first = normalizeTaxMaintenanceRecord(
    maintenances[0] as Record<string, unknown>,
  );

  return {
    fromDate: first.fromDate || null,
    toDate: first.toDate || null,
    isFlat: first.isTaxAmount,
    taxPercent: first.isTaxAmount ? null : first.taxAmount,
    flatAmount: first.isTaxAmount ? first.taxAmount : null,
  };
};
